import { useMemo, useState } from "react";
import { Minus, Package, Plus, Truck, X } from "lucide-react";
import { useTranslation } from "../../i18n";
import type { DispatchOrder, DispatchOrderItem, Parcel } from "../../types/Dispatch";
import { useAccess } from "../../access/AccessContext";
import { displayItemCode } from "./item-code";
import type { RefundLine } from "./flagged-refund-modal";
import { userIsManagerPlus } from "./flagged-resolve-split-button";

// Quick "Manually fulfil" from the flagged list — for the case where the
// marked-short units turn out to have actually gone out in one of the
// parcels the desk already sent (miscount, or found and thrown in at the
// last moment). The leader picks WHICH parcel number the items are being
// added to; the BE then corrects the record — the parcel's contents gain
// the units and the line counters move from missing/damaged/wrong into
// shipped. One parcel for all the shipped items; anything that genuinely
// split across boxes goes through the full Dispatch route.
//
// Not every outstanding unit is always found (per Alex, 2026-10-27), so
// each line has a stepper, prefilled with everything outstanding. Units
// stepped off did NOT ship: they go to Pending Refund in the same
// resolve, exactly as the quick Refund would put them — the flag closes
// either way, so they cannot be left outstanding. That makes the request
// a refund to the BE (flaggedResolveKey), so a leader without the refund
// key can only confirm with every unit shipped.
//
// Only offered for orders that HAVE parcels (auto-flagged, shipped
// short); the resolve menu hides it otherwise. Layout mirrors the quick
// Refund modal: the same line cards for the items, then the parcel picker.

export type ShipLine = { line_position: number | undefined; count: number };

type Props = {
    order: DispatchOrder;
    busy: boolean;
    onCancel: () => void;
    // refundLines carries only the units stepped off, empty when all ship.
    onConfirm: (trackingNumber: string, shipLines: ShipLine[], refundLines: RefundLine[], refundTotal: number) => void;
};

const outstandingOf = (i: DispatchOrderItem) =>
    (i.missingCount || 0) + (i.damagedCount || 0) + (i.wrongItemCount || 0);

const formatWeight = (grams?: number) =>
    typeof grams === "number" && grams > 0 ? `${(grams / 1000).toFixed(2)} kg` : null;

const formatMoney = (value: number, currency: string) => {
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
    } catch {
        return `${currency} ${value.toFixed(2)}`;
    }
};

const FlaggedManualShipModal = ({ order, busy, onCancel, onConfirm }: Props) => {
    const { t } = useTranslation();
    const { authoritative, canWrite } = useAccess();
    const canRefund = authoritative ? canWrite("dispatch.flagged.refund") : userIsManagerPlus();
    const currency = order.currency || "GBP";
    const parcels = useMemo<Parcel[]>(() => order.parcels ?? [], [order]);
    const [selected, setSelected] = useState<string>(parcels[0]?.tracking_number ?? "");

    const lines = useMemo(
        () => order.items
            .map(i => ({ item: i, cap: outstandingOf(i) }))
            .filter(l => l.cap > 0),
        [order],
    );

    const [counts, setCounts] = useState<Record<string, number>>(
        () => Object.fromEntries(lines.map(l => [l.item.item_id, l.cap])),
    );
    const countOf = (id: string) => counts[id] ?? 0;
    const step = (id: string, cap: number, delta: number) =>
        setCounts(prev => ({ ...prev, [id]: Math.min(cap, Math.max(0, (prev[id] ?? 0) + delta)) }));

    const shipUnits = lines.reduce((s, l) => s + countOf(l.item.item_id), 0);
    const refundUnits = lines.reduce((s, l) => s + l.cap - countOf(l.item.item_id), 0);
    const refundTotal = lines.reduce(
        (s, l) => s + (l.cap - countOf(l.item.item_id)) * (l.item.unit_price || 0), 0);
    const refundBlocked = refundUnits > 0 && !canRefund;

    // Mirror of the BE's status derivation, as the Refund modal shows it.
    const totalQuantity = order.items.reduce((s, i) => s + i.quantity, 0);
    const resultStatus = refundUnits === 0
        ? t("status_fulfilled")
        : refundUnits >= totalQuantity
            ? t("status_refunded")
            : t("status_partially_refunded");

    const confirm = () => {
        const withPos = lines.filter(l => l.item.line_position !== undefined);
        const shipLines: ShipLine[] = withPos
            .filter(l => countOf(l.item.item_id) > 0)
            .map(l => ({ line_position: l.item.line_position, count: countOf(l.item.item_id) }));
        const refundLines: RefundLine[] = withPos
            .filter(l => countOf(l.item.item_id) < l.cap)
            .map(l => ({ line_position: l.item.line_position, refund_count: l.cap - countOf(l.item.item_id) }));
        onConfirm(selected, shipLines, refundLines, refundTotal);
    };

    return (
        <div className="modal-overlay" onClick={busy ? undefined : onCancel}>
            <div className="modal-card dispatch-flagged-action-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("manual_ship_title", { order: order.order_number })}</h2>
                    <button className="modal-close" onClick={onCancel} disabled={busy} aria-label={t("cancel")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    {/* What will be recorded as shipped — same line cards
                        as the refund modal. Stepped down = not found. */}
                    <p className="dispatch-flagged-action-intro">{t("manual_ship_items_note")}</p>
                    <ul className="dispatch-replacement-lines">
                        {lines.map(({ item, cap }) => {
                            const n = countOf(item.item_id);
                            return (
                                <li key={item.item_id} className="dispatch-replacement-line">
                                    <span className="dispatch-replacement-thumb" aria-hidden="true">
                                        {item.image_url ? (
                                            <img
                                                src={item.image_url}
                                                alt=""
                                                loading="lazy"
                                                onError={e => { e.currentTarget.style.display = "none"; }}
                                            />
                                        ) : (
                                            <Package size={20} />
                                        )}
                                    </span>
                                    <span className="dispatch-replacement-info">
                                        <span className="dispatch-replacement-title">{item.title || item.sku}</span>
                                        <span className="dispatch-replacement-sku">{displayItemCode(item.sku, item.barcode)}</span>
                                        <span className="dispatch-replacement-stock">
                                            {t("manual_ship_line_outstanding", { n: String(cap) })}
                                            {n < cap && (
                                                <>{" · "}{t("manual_ship_line_to_refund", { n: String(cap - n) })}</>
                                            )}
                                        </span>
                                    </span>
                                    <span className="dispatch-replacement-stepper">
                                        <button
                                            type="button"
                                            onClick={() => step(item.item_id, cap, -1)}
                                            disabled={busy || n <= 0}
                                            aria-label="-1"
                                        >
                                            <Minus size={16} />
                                        </button>
                                        <span className="dispatch-replacement-qty">{n}</span>
                                        <button
                                            type="button"
                                            onClick={() => step(item.item_id, cap, 1)}
                                            disabled={busy || n >= cap}
                                            aria-label="+1"
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </span>
                                </li>
                            );
                        })}
                    </ul>

                    {/* Which parcel number they are being added to. */}
                    <p className="dispatch-flagged-action-intro">{t("manual_ship_pick_parcel")}</p>
                    <div className="dispatch-flagged-parcel-options" role="radiogroup" aria-label={t("manual_ship_pick_parcel")}>
                        {parcels.map((p, idx) => {
                            const weight = formatWeight(p.weight_grams);
                            const checked = selected === p.tracking_number;
                            return (
                                <label
                                    key={p.tracking_number}
                                    className={`dispatch-flagged-parcel-option${checked ? " dispatch-flagged-parcel-option--on" : ""}`}
                                >
                                    <input
                                        type="radio"
                                        name="flagged-manual-ship-parcel"
                                        value={p.tracking_number}
                                        checked={checked}
                                        disabled={busy}
                                        onChange={() => setSelected(p.tracking_number)}
                                    />
                                    <span className="dispatch-flagged-parcel-option-number" aria-hidden="true">
                                        {p.box_position ?? idx + 1}
                                    </span>
                                    <span className="dispatch-flagged-parcel-option-info">
                                        <span className="dispatch-flagged-parcel-option-title">
                                            {t("parcel")} {p.box_position ?? idx + 1}/{parcels.length}
                                            {" · "}{p.carrier}
                                            {weight ? ` · ${weight}` : ""}
                                        </span>
                                        <span className="dispatch-flagged-parcel-option-sub">
                                            {p.tracking_number}
                                            {p.packed_by_name ? ` · ${p.packed_by_name}` : ""}
                                        </span>
                                    </span>
                                </label>
                            );
                        })}
                    </div>

                    {/* Only once something has been stepped off: where the
                        rest goes, and what the order will settle as. */}
                    {shipUnits === 0 ? (
                        <p className="dispatch-flagged-action-result dispatch-flagged-action-result--warn">
                            {t("manual_ship_none_selected")}
                        </p>
                    ) : refundUnits > 0 && (
                        <p className={`dispatch-flagged-action-result${refundBlocked ? " dispatch-flagged-action-result--warn" : ""}`}>
                            {refundBlocked
                                ? t("manual_ship_refund_not_permitted")
                                : <>
                                    {t("manual_ship_rest_to_refund", {
                                        n: String(refundUnits),
                                        amount: formatMoney(refundTotal, currency),
                                    })}
                                    {" "}
                                    {t("resolve_status_result", { status: resultStatus })}
                                </>}
                        </p>
                    )}

                    <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                        <button className="btn btn-ghost dispatch-modal-btn" onClick={onCancel} disabled={busy}>
                            {t("cancel")}
                        </button>
                        <button
                            className="btn btn-success dispatch-modal-btn"
                            onClick={confirm}
                            disabled={busy || !selected || shipUnits === 0 || refundBlocked}
                        >
                            <Truck size={14} />
                            {busy ? t("submitting") : t("manual_ship_confirm")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FlaggedManualShipModal;
